import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Theme } from '@mui/material';

import {
  AccountButtons,
  Header,
  NavBar,
  NavBarItem,
  NavDrawer,
  NavDrawerItem,
} from '@common/components/Header';
import { useIsMobile } from '@common/utils/hooks';
import PointsPortalImg from '@campaign/assets/images/points-portal.svg';
import CampaignCustomerImg from '@campaign/assets/images/redeem.svg';
import { buildRegisterRedirectUrl } from '@campaign/utils/idpfRedirect';

const PCC_CAMPAIGN_CUSTOMER_URL = `${import.meta.env.PCC_CAMPAIGN_CUSTOMER_URL}`;
const PCC_POINTS_PORTAL_URL = `${import.meta.env.PCC_POINTS_PORTAL_URL}`;

export type CampaignHeaderProps = {
  name: string;
  onClickLogin: () => void;
  onClickLogout: () => void;
  onClickMemberProfileMenuItem: () => void;
  userIsLoggedIn: boolean;
};

/**
 * Campaign site header that composes the account bar with nav links to the
 * points portal and campaign site.
 *
 * @param props - The component props.
 * @param props.name - Display name shown in the account bar when logged in.
 * @param props.onClickLogin - Handler fired when the login button is clicked.
 * @param props.onClickLogout - Handler fired when the logout menu item is clicked.
 * @param props.onClickMemberProfileMenuItem - Handler fired when the member
 *   profile menu item is clicked.
 * @param props.userIsLoggedIn - Whether the user is currently authenticated;
 *   controls which account controls are shown.
 * @returns The rendered campaign header.
 */
export function CampaignHeader({
  name,
  onClickLogin,
  onClickLogout,
  onClickMemberProfileMenuItem,
  userIsLoggedIn,
}: CampaignHeaderProps) {
  const isMobile = useIsMobile();
  const onClickPointsLink = () => {
    window.open(PCC_POINTS_PORTAL_URL, '_blank', 'noopener,noreferrer');
  };
  const onClickCampaignLink = () => {
    window.location.assign(PCC_CAMPAIGN_CUSTOMER_URL);
  };
  const onClickSignUp = () => {
    window.open(buildRegisterRedirectUrl(), '_blank', 'noopener,noreferrer');
  };
  const navLinks = [
    {
      icon: <img src={PointsPortalImg} alt="Points Portal icon" />,
      label: '明治ポイントを使う・貯める',
      onClick: onClickPointsLink,
    },
    {
      icon: <img src={CampaignCustomerImg} alt="Campaign Customer icon" />,
      label: '明治会員ID限定キャンペーン',
      onClick: onClickCampaignLink,
    },
  ];

  return (
    <Header>
      {!isMobile && (
        <NavBar>
          {navLinks.map(({ label, onClick }) => (
            <NavBarItem
              key={label}
              icon={<NavBarIcon />} // Use override icon
              label={label}
              onClick={onClick}
            />
          ))}
        </NavBar>
      )}
      <AccountButtons
        name={name}
        onClickLogin={onClickLogin}
        onClickLogout={onClickLogout}
        onClickMemberProfileMenuItem={onClickMemberProfileMenuItem}
        onClickSignUp={onClickSignUp}
        userIsLoggedIn={userIsLoggedIn}
      />
      {isMobile && (
        <NavDrawer>
          {navLinks.map(({ icon, label, onClick }) => (
            <NavDrawerItem
              key={label}
              icon={icon}
              label={label}
              onClick={onClick}
            />
          ))}
        </NavDrawer>
      )}
    </Header>
  );
}

/**
 * Override icon used for each `NavBarItem` in the campaign header.
 */
function NavBarIcon() {
  return (
    <ChevronRightIcon
      sx={(theme: Theme) => ({
        color: theme.colors.primary,
        height: '24px',
        width: '24px',
      })}
    />
  );
}
